/**
 * A fala da VecchIA que ainda está chegando: PCM 16 bits, mono, little-endian, como o Gemini gera e a
 * borda repassa (`brain-voz/falar` com `fluxo`, B11).
 *
 * Guarda tudo o que chegou, em amostras de ponto flutuante: a reprodução agenda daqui conforme
 * cresce, retoma de onde parou depois de uma pausa, e a frase curta guardada em cache toca de novo
 * sem pedir outra vez. Um byte que chega sozinho no fim de um pedaço da rede espera o par no
 * seguinte — sem isso, toda amostra dali em diante sairia trocada, em chiado.
 *
 * Sem React. Testado em `tests/unit/brain-fluxo-de-pcm.test.ts`.
 */

export class FluxoDePcm {
    private readonly pedacos: Float32Array[] = [];
    private readonly ouvintes = new Set<() => void>();
    private sobra: number | null = null;
    private total = 0;
    private acabou = false;
    private interrompido = false;
    private resolverFim: () => void = () => undefined;
    private readonly fim = new Promise<void>((resolver) => { this.resolverFim = resolver; });

    constructor(readonly taxa: number) {}

    /** Lê o corpo da resposta em segundo plano; devolve na hora, antes do primeiro pedaço. */
    static lendo(corpo: ReadableStream<Uint8Array>, taxa: number): FluxoDePcm {
        const fluxo = new FluxoDePcm(taxa);
        const leitor = corpo.getReader();
        void (async () => {
            try {
                for (;;) {
                    const { done, value } = await leitor.read();
                    if (done) break;
                    fluxo.empurrar(value);
                }
                fluxo.terminar();
            } catch {
                /* A conexão caiu no meio: o que chegou toca, e `falhou` diz que não é a fala inteira. */
                fluxo.terminar(true);
            }
        })();
        return fluxo;
    }

    get amostras(): number {
        return this.total;
    }

    get terminou(): boolean {
        return this.acabou;
    }

    get falhou(): boolean {
        return this.interrompido;
    }

    empurrar(bytes: Uint8Array): void {
        if (this.acabou || !bytes.length) return;
        let dados = bytes;
        if (this.sobra !== null) {
            dados = new Uint8Array(bytes.length + 1);
            dados[0] = this.sobra;
            dados.set(bytes, 1);
            this.sobra = null;
        }
        const pares = dados.length - (dados.length % 2);
        if (pares < dados.length) this.sobra = dados[dados.length - 1] ?? null;
        if (!pares) return;
        const leitura = new DataView(dados.buffer, dados.byteOffset, pares);
        const amostras = new Float32Array(pares / 2);
        for (let i = 0; i < amostras.length; i += 1) amostras[i] = leitura.getInt16(i * 2, true) / 32768;
        this.pedacos.push(amostras);
        this.total += amostras.length;
        this.avisar();
    }

    terminar(falhou = false): void {
        if (this.acabou) return;
        this.acabou = true;
        this.interrompido = falhou;
        this.sobra = null;
        this.resolverFim();
        this.avisar();
    }

    /** Chamado a cada pedaço novo e no fim. Devolve quem para de ouvir. */
    ouvir(ouvinte: () => void): () => void {
        this.ouvintes.add(ouvinte);
        return () => { this.ouvintes.delete(ouvinte); };
    }

    terminado(): Promise<void> {
        return this.fim;
    }

    /** As amostras de `de` até `ate` (exclusivo), atravessando os pedaços. */
    copiar(de: number, ate: number): Float32Array {
        const inicio = Math.max(0, Math.floor(de));
        const fim = Math.min(this.total, Math.floor(ate));
        const saida = new Float32Array(Math.max(0, fim - inicio));
        let base = 0;
        for (const pedaco of this.pedacos) {
            const depois = base + pedaco.length;
            if (depois > inicio && base < fim) {
                const a = Math.max(inicio, base) - base;
                const b = Math.min(fim, depois) - base;
                saida.set(pedaco.subarray(a, b), base + a - inicio);
            }
            if (depois >= fim) break;
            base = depois;
        }
        return saida;
    }

    /** O que chegou, em WAV: para o `<audio>` de navegador sem Web Audio. */
    emWav(): Blob {
        const amostras = this.copiar(0, this.total);
        const wav = new ArrayBuffer(44 + amostras.length * 2);
        const v = new DataView(wav);
        const escrever = (p: number, s: string) => { for (let i = 0; i < s.length; i += 1) v.setUint8(p + i, s.charCodeAt(i)); };
        escrever(0, 'RIFF');
        v.setUint32(4, 36 + amostras.length * 2, true);
        escrever(8, 'WAVE');
        escrever(12, 'fmt ');
        v.setUint32(16, 16, true);
        v.setUint16(20, 1, true);
        v.setUint16(22, 1, true);
        v.setUint32(24, this.taxa, true);
        v.setUint32(28, this.taxa * 2, true);
        v.setUint16(32, 2, true);
        v.setUint16(34, 16, true);
        escrever(36, 'data');
        v.setUint32(40, amostras.length * 2, true);
        /* A volta exata da leitura (÷ 32768): multiplicar por 32767 e truncar tirava 1 de cada amostra positiva. */
        for (let i = 0; i < amostras.length; i += 1) {
            v.setInt16(44 + i * 2, Math.max(-32768, Math.min(32767, Math.round((amostras[i] ?? 0) * 32768))), true);
        }
        return new Blob([wav], { type: 'audio/wav' });
    }

    private avisar(): void {
        for (const ouvinte of [...this.ouvintes]) ouvinte();
    }
}
